// src/pages/Dashboard.js
import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { ProductsAPI } from "../api/productApi";
import Footer from "../components/Footer";
import { Line, Pie } from "react-chartjs-2";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement, // Add PointElement here
  LineElement, // Add LineElement here
  ArcElement, // Add ArcElement here (for pie charts)
} from "chart.js";
import { getSaleHistory } from "../api/salesApi";
import SaleHistoryComponent from "./sale/saleHistory/SaleHistoryComponent";
import { useNavigate } from "react-router-dom";
import { fetchMonthlySalesReportsData } from "../api/reportsApi";

// Register the necessary components for chart.js v3+
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement
);

const Dashboard = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [stockData, setStockData] = useState({});

  // Fetch products from the API
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await ProductsAPI.list();
        setProducts(data);
        processStockData(data);
      } catch (err) {
        setError("Failed to load products.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);
  async function exampleFetchSales(q) {
    const params = {
      page: q.page,
      pageSize: q.pageSize,
    };

    if (q.q) params.q = q.q;
    if (q.dateFrom) params.dateFrom = q.dateFrom;
    if (q.dateTo) params.dateTo = q.dateTo;
    if (q.status) params.status = q.status;
    if (q.sort && q.sort.field) {
      params.sortField = q.sort.field;
      params.sortDir = q.sort.dir;
    }

    const res = await getSaleHistory(params);
    // if (!res.ok) throw new Error("Failed to fetch sales");
    // return res.json();
    return res;
  }

  useEffect(() => {
    const loadSalesData = async () => {
      try {
        const data = await fetchMonthlySalesReportsData({
          page: 1,
          pageSize: 12,
        });
        const monthlyData = data.map((sale) => ({
          month: sale.month,
          sales: sale.totalRevenue,
        }));
        setSalesData(monthlyData);
      } catch (err) {
        console.error("Failed to load sales data", err);
      }
    };

    loadSalesData();
  }, []); // Only run once when the component mounts

  const navigate = useNavigate();
  // Process stock data to be used in the pie chart
  const processStockData = (products) => {
    const categories = products.reduce((acc, product) => {
      if (acc[product.name]) {
        acc[product.name] += product.on_hand_units;
      } else {
        acc[product.name] = product.on_hand_units;
      }
      return acc;
    }, {});

    setStockData(categories);
  };

  // Chart data for stock distribution
  const stockChartData = {
    labels: Object.keys(stockData),
    datasets: [
      {
        label: "Stock Distribution by Category",
        data: Object.values(stockData),
        backgroundColor: [
          "#4A90E2",
          "#E94E77",
          "#50E3C2",
          "#F8E71C",
          "#7B9B7F",
        ],
        borderColor: "#FFFFFF",
        borderWidth: 2,
      },
    ],
  };

  // Sales line chart (monthly sales)
  const salesChartData = {
    labels: salesData.map((data) => data.month),
    datasets: [
      {
        label: "Sales in INR",
        data: salesData.map((data) => data.sales),
        fill: true,
        backgroundColor: "rgba(75,192,192,0.4)", // Light teal background
        borderColor: "rgba(75,192,192,1)", // Teal border
        borderWidth: 2, // Slightly thicker border
        pointBackgroundColor: "rgba(75,192,192,1)", // Circle color
        pointBorderColor: "#fff", // White border for the points
        pointRadius: 5, // Larger point radius for better visibility
        pointHoverRadius: 8, // Slightly larger radius when hovering over a point
      },
    ],
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-lg text-gray-700">Loading products...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-lg text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="max-w-screen-xl mx-auto p-4">
        <h1 className="text-3xl font-bold text-center mb-6">
          Beverage Distribution Dashboard
        </h1>

        {/* Graphs Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-10">
          <div className="bg-white shadow-lg rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">
              Inventory Balance by Product
            </h3>
            <Bar
              data={{
                labels: Object.keys(stockData),
                datasets: [
                  {
                    label: "Units on Hand",
                    data: Object.values(stockData),
                    backgroundColor: "rgba(75, 192, 192, 0.6)",
                    borderColor: "rgba(75, 192, 192, 1)",
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                indexAxis: "x", // Horizontal bar chart
                responsive: true,
                scales: {
                  x: { beginAtZero: true },
                },
              }}
            />
          </div>

          <div className="bg-white shadow-lg rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">
              Sales Overview (Monthly)
            </h3>
            <Line data={salesChartData}  />
          </div>
        </div>
        <SaleHistoryComponent
          fetchSales={exampleFetchSales}
          onRowClick={(e) => navigate(`/sale/${e?.id}`)}
        />
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;
